import dayjs from "dayjs";
import { useEffect } from "react";
import { request } from "../../utils/request";
import {
	SENT_FIELD,
	WEBHOOK_FIELD,
	autoSendBirthdayZns,
	getBirthdayVN,
	getPeriodKeys,
	isValidVnMobile,
} from "../../utils/utilsBirthday";
import { createPollerLock } from "../../utils/utilsPollerLock";

const POLL_INTERVAL_MS = 15 * 60 * 1000;
const LOCK_TTL_MS = 20 * 60 * 1000;
const tryAcquireLock = createPollerLock("birthday_poller_lock", LOCK_TTL_MS);

// Chi gui trong khung 09:00 - 19:00 gio Viet Nam
const SEND_FROM_HOUR = 9;
const SEND_TO_HOUR = 19;
const VN_OFFSET_MINUTES = 7 * 60;

const MAX_FETCH = 2000;
const PAGE_SIZE = 200;
const MAX_SEND_PER_SCAN = 50;
const THROTTLE_MS = 400;
// Gui loi thi khong thu lai ngay trong lan quet sau, doi 1 gio de khong spam webhook
const RETRY_AFTER_FAIL_MS = 60 * 60 * 1000;

const failedAt = new Map();
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getHeaders = (token) => ({
	"X-Access-Token": token,
	"Content-Type": "application/json",
});

const fetchAppExfields = async (idApp, token) => {
	try {
		const res = await request.get("api/app", {
			params: { q: JSON.stringify({ _id: idApp }), notfields: "__v" },
			headers: getHeaders(token),
		});
		return res?.data?.[0]?.exfields || {};
	} catch (e) {
		console.error("Khong lay duoc cau hinh webhook sinh nhat", e);
		return null;
	}
};

// Khach hien huu co sinh nhat trong thang hien tai (loc tho thang +-1 tren server,
// loc chinh xac theo gio Viet Nam o client)
const fetchCustomers = async (idApp, token, month) => {
	const months = [((month + 10) % 12) + 1, month, (month % 12) + 1];
	const q = {
		trang_thai: 1,
		status: true,
		isbuyer: true,
		kh_yn: true,
		loai_kh: { $nin: ["Vendor", "Staff"] },
		$expr: { $in: [{ $month: "$ngay_sinh" }, months] },
	};
	let all = [];
	let page = 1;
	while (true) {
		const res = await request.get(`api/${idApp}/customer`, {
			params: { q: JSON.stringify(q), limit: PAGE_SIZE, page },
			headers: getHeaders(token),
		});
		const rows = Array.isArray(res?.data) ? res.data : [];
		all = all.concat(rows);
		if (rows.length < PAGE_SIZE || all.length >= MAX_FETCH) break;
		page += 1;
	}
	return all;
};

export default function BirthdayPoller() {
	useEffect(() => {
		let cancelled = false;

		const tick = async () => {
			const token = localStorage.getItem("token");
			const idApp = localStorage.getItem("id_app");
			if (!token || !idApp) return;

			const nowVN = dayjs().utcOffset(VN_OFFSET_MINUTES);
			if (nowVN.hour() < SEND_FROM_HOUR || nowVN.hour() >= SEND_TO_HOUR) {
				return;
			}
			if (!tryAcquireLock()) return;

			const appExfields = await fetchAppExfields(idApp, token);
			if (!appExfields) return;
			const linkThang = appExfields[WEBHOOK_FIELD.thang];
			const linkNgay = appExfields[WEBHOOK_FIELD.ngay];
			if (!linkThang && !linkNgay) return;

			try {
				const customers = await fetchCustomers(
					idApp,
					token,
					nowVN.month() + 1,
				);
				const year = nowVN.year();
				const todayKey = nowVN.format("YYYY-MM-DD");
				let sentCount = 0;

				for (const customer of customers) {
					if (cancelled) return;
					if (sentCount >= MAX_SEND_PER_SCAN) break;

					const birthday = getBirthdayVN(customer?.ngay_sinh);
					if (!birthday || birthday.month() !== nowVN.month()) continue;
					// SDT khong hop le thi bo qua, khong ghi co
					if (!isValidVnMobile(customer?.dien_thoai)) continue;

					const keys = getPeriodKeys(birthday, year);
					const jobs = [];
					if (
						linkThang &&
						customer?.exfields?.[SENT_FIELD.thang] !== keys.month
					) {
						jobs.push({
							type: "thang",
							periodKey: keys.month,
							linkWebhook: linkThang,
						});
					}
					if (
						linkNgay &&
						keys.day === todayKey &&
						customer?.exfields?.[SENT_FIELD.ngay] !== keys.day
					) {
						jobs.push({
							type: "ngay",
							periodKey: keys.day,
							linkWebhook: linkNgay,
						});
					}

					for (const job of jobs) {
						if (cancelled) return;
						const failKey = `${customer._id}_${job.type}_${job.periodKey}`;
						const lastFail = failedAt.get(failKey);
						if (lastFail && Date.now() - lastFail < RETRY_AFTER_FAIL_MS) {
							continue;
						}
						const result = await autoSendBirthdayZns({
							...job,
							customer,
							birthday,
							idApp,
							token,
						});
						if (result === "failed") failedAt.set(failKey, Date.now());
						if (result === "sent") sentCount += 1;
						await sleep(THROTTLE_MS);
					}
				}
			} catch (e) {
				console.error("Quet sinh nhat loi", e);
			}
		};

		tick();
		const intervalId = setInterval(tick, POLL_INTERVAL_MS);
		return () => {
			cancelled = true;
			clearInterval(intervalId);
		};
	}, []);

	return null;
}
